import React from "react";

export const StatCard = ({ label, value, accent = "magenta", icon: Icon, testId }) => {
  const tone = accent === "cyan"
    ? "from-[#18d3ff]/20 to-transparent text-[#7fe6ff]"
    : "from-[#ff2e93]/20 to-transparent text-[#ff7fc1]";
  return (
    <div data-testid={testId} className="relative glass chrome-border rounded-md p-5 lift overflow-hidden">
      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${tone} opacity-40 pointer-events-none`} />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2">{label}</p>
          <p className="font-display text-3xl sm:text-4xl text-chrome leading-none">{value}</p>
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-full chrome-border bg-black/40 flex items-center justify-center shrink-0">
            <Icon className={`w-4 h-4 ${accent === "cyan" ? "text-[#18d3ff]" : "text-[#ff2e93]"}`} />
          </div>
        )}
      </div>
    </div>
  );
};

export const AdminInput = React.forwardRef(({ label, icon: Icon, error, className = "", ...rest }, ref) => (
  <label className={`block ${className}`}>
    {label && (
      <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </span>
    )}
    <input
      ref={ref}
      {...rest}
      className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none font-body transition-all focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]"
    />
    {error && <span className="text-xs text-[#ff7fc1] mt-1 block">{error}</span>}
  </label>
));
AdminInput.displayName = "AdminInput";

export const AdminTextarea = ({ label, rows = 4, className = "", ...rest }) => (
  <label className={`block ${className}`}>
    {label && <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 block">{label}</span>}
    <textarea
      {...rest}
      rows={rows}
      className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none font-body transition-all focus:border-[#18d3ff] focus:shadow-[0_0_0_3px_rgba(24,211,255,0.10)] resize-none"
    />
  </label>
);

export const AdminSelect = ({ label, children, className = "", ...rest }) => (
  <label className={`block ${className}`}>
    {label && <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 block">{label}</span>}
    <select
      {...rest}
      className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white outline-none font-body transition-all focus:border-[#ff2e93]"
    >
      {children}
    </select>
  </label>
);

export const StatusPill = ({ status }) => {
  const map = {
    draft:     "border-white/15 text-white/60",
    published: "border-[#18d3ff]/40 text-[#7fe6ff] bg-[#18d3ff]/10",
    archived:  "border-white/10 text-white/40",
    active:    "border-[#18d3ff]/40 text-[#7fe6ff] bg-[#18d3ff]/10",
    inactive:  "border-white/10 text-white/40",
    hidden:    "border-white/10 text-white/40",
    new:       "border-[#ff2e93]/40 text-[#ff9fcf] bg-[#ff2e93]/10",
    read:      "border-white/15 text-white/60",
    replied:   "border-[#18d3ff]/40 text-[#7fe6ff] bg-[#18d3ff]/10",
    contacted: "border-white/15 text-white/60",
    quoted:    "border-[#ff2e93]/40 text-[#ff9fcf] bg-[#ff2e93]/10",
    booked:    "border-[#18d3ff]/40 text-[#7fe6ff] bg-[#18d3ff]/10",
    completed: "border-[#18d3ff]/40 text-[#7fe6ff] bg-[#18d3ff]/10",
  };
  const cls = map[status] || "border-white/10 text-white/50";
  return (
    <span className={`inline-flex items-center text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full border ${cls}`}>
      {status || "—"}
    </span>
  );
};

export const EmptyState = ({ title, message, testId }) => (
  <div data-testid={testId} className="glass chrome-border rounded-md p-10 text-center">
    <p className="font-display text-2xl text-chrome">{title}</p>
    {message && <p className="mt-2 text-sm text-white/55 font-body max-w-md mx-auto">{message}</p>}
  </div>
);

export const Panel = ({ title, action, children, className = "", testId }) => (
  <section data-testid={testId} className={`glass chrome-border rounded-md p-5 sm:p-6 ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-4 mb-5">
        {title && <h2 className="font-display text-xl text-chrome">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </section>
);
